import { Injectable, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ChangelogData, ChangelogStatus, ChangelogVersion } from '../model/changelog.models';


@Injectable({
    providedIn: 'root'  // Disponible en toda la app
})
export class ChangelogService {

    // ====== SIGNALS (Estado Reactivo) ======

    // Signal privado: los datos del changelog
    private _changelogData = signal<ChangelogData | null>(null);

    // Signal privado: estado de carga
    private _status = signal<ChangelogStatus>('idle');

    // Signal privado: mensaje de error
    private _error = signal<string | null>(null);

    // Signal privado: término de búsqueda actual
    private _searchQuery = signal<string>('');

    // Signal privado: categoría filtrada
    private _filterCategory = signal<string | null>(null);

    // ====== COMPUTED (Valores Derivados) ======

    // Computed: todas las versiones (solo lectura)
    readonly versions = computed(() => {
        return this._changelogData()?.versions ?? [];
    });

    // Computed: estado actual
    readonly status = computed(() => this._status());

    // Computed: error actual
    readonly error = computed(() => this._error());

    // Computed: versiones filtradas por búsqueda y categoría
    readonly filteredVersions = computed(() => {
        let versions = this.versions();
        const query = this._searchQuery().toLowerCase();
        const category = this._filterCategory();

        // Filtrar por categoría
        if (category) {
            versions = versions.filter(v =>
                Object.keys(v.categories).includes(category)
            );
        }

        // Filtrar por búsqueda
        if (query) {
            versions = versions.filter(v => {
                // Buscar en versión
                if (v.version.toLowerCase().includes(query)) return true;

                // Buscar en cambios
                for (const entries of Object.values(v.categories)) {
                    if (entries.some(e => e.toLowerCase().includes(query))) {
                        return true;
                    }
                }
                return false;
            });
        }

        return versions;
    });

    // ====== CONSTRUCTOR ======

    constructor(private http: HttpClient) {}

    // ====== MÉTODOS PÚBLICOS ======

    /**
     * Carga el changelog desde el JSON
     */
    loadChangelog(): void {
        // Evitar cargas duplicadas
        if (this._status() === 'loading') return;

        this._status.set('loading');
        this._error.set(null);

        this.http.get<ChangelogData>('/assets/changelog.json').subscribe({
            next: (data) => {
                this._changelogData.set(data);
                this._status.set('success');
            },
            error: (err) => {
                console.error('Error cargando changelog:', err);
                this._error.set('No se pudo cargar el historial de cambios');
                this._status.set('error');
            }
        });
    }

    /**
     * Obtiene las últimas N versiones
     */
    getLatestVersions(count: number): ChangelogVersion[] {
        const versions = this.versions();
        // Filtrar "Unreleased" y tomar las primeras N
        return versions
            .filter(v => v.version !== 'Unreleased')
            .slice(0, count);
    }

    /**
     * Establece el filtro de categoría
     */
    filterByCategory(category: string | null): void {
        this._filterCategory.set(category);
    }

    /**
     * Establece el término de búsqueda
     */
    searchChanges(query: string): void {
        this._searchQuery.set(query);
    }

    /**
     * Limpia todos los filtros
     */
    clearFilters(): void {
        this._searchQuery.set('');
        this._filterCategory.set(null);
    }

    /**
     * Obtiene todas las categorías disponibles
     */
    getAvailableCategories(): string[] {
        const categories = new Set<string>();

        for (const version of this.versions()) {
            for (const cat of Object.keys(version.categories)) {
                categories.add(cat);
            }
        }

        return Array.from(categories).sort();
    }
}
